import { useState } from 'react'
import { useCountryGroups } from '@/hooks/use-address-lists'
import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { FieldLabelRow } from './field-label-row'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { useLingui } from '@lingui/react/macro'
import { cn } from '@/lib/utils'
import { COUNTRY_FLAGS } from '@/components/custom/country-flags'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

function CountryFlag(props: { code: string; className?: string }) {
  const Flag = COUNTRY_FLAGS[props.code]
  if (!Flag) return null
  return <Flag className={cn('size-4 shrink-0 rounded-[2px]', props.className)} />
}

type CountryOption = { value: string; label: string }

const filterByLabel = (items: CountryOption[], query: string) => {
  if (!query) return items
  const s = query.toLowerCase()
  return items.filter((i) => i.label.toLowerCase().includes(s))
}

// Pair with `ProvinceField`: this field's `listeners` clear the province when the country changes.
export default function CountryField(props: {
  label: string
  className?: string
  placeholder?: string
  optional?: boolean
  orientation?: 'vertical' | 'horizontal'
}) {
  const { t } = useLingui()
  const field = useFieldContext<string>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'
  const [searchValue, setSearchValue] = useState('')

  const groups = useCountryGroups()
  const priorityItems = groups.priority
  const otherItems = groups.other
  const allItems = [...priorityItems, ...otherItems]

  const selectedItem = allItems.find((i) => i.value === field.state.value) ?? null

  const filteredPriority = filterByLabel(priorityItems, searchValue)
  const filteredOther = filterByLabel(otherItems, searchValue)

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          <Combobox
            items={allItems}
            value={selectedItem}
            onValueChange={(item: CountryOption | null) => {
              if (item === null && allItems.length === 0) return
              field.handleChange(item?.value ?? '')
            }}
            onInputValueChange={setSearchValue}
            isItemEqualToValue={(a: CountryOption, b: CountryOption) => a.value === b.value}
          >
            <ComboboxTrigger
              render={
                <Button
                  id={field.name}
                  type="button"
                  variant="outline"
                  aria-invalid={hasFieldError(field.state.meta) || undefined}
                  className={cn('h-8 w-full justify-between font-normal', !selectedItem && 'text-muted-foreground')}
                />
              }
            >
              {selectedItem ? (
                <span className="flex items-center gap-2 truncate">
                  <CountryFlag code={selectedItem.value} />
                  {selectedItem.label}
                </span>
              ) : (
                <span>{formReadOnly ? '-' : props.placeholder}</span>
              )}
            </ComboboxTrigger>
            <ComboboxContent className="min-w-(--anchor-width)">
              <ComboboxInput showTrigger={false} placeholder={t`Search...`} />
              <ComboboxList>
                {filteredPriority.length > 0 && (
                  <ComboboxGroup>
                    <ComboboxLabel>{t`Popular`}</ComboboxLabel>
                    {filteredPriority.map((item) => (
                      <ComboboxItem key={item.value} value={item}>
                        <CountryFlag code={item.value} />
                        {item.label}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                )}
                {filteredPriority.length > 0 && filteredOther.length > 0 && <ComboboxSeparator />}
                {filteredOther.length > 0 && (
                  <ComboboxGroup>
                    <ComboboxLabel>{t`All countries`}</ComboboxLabel>
                    {filteredOther.map((item) => (
                      <ComboboxItem key={item.value} value={item}>
                        {item.label}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                )}
              </ComboboxList>
              <ComboboxEmpty>{t`No results found`}</ComboboxEmpty>
            </ComboboxContent>
          </Combobox>
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
